<script setup lang="ts">
/**
 * Modal Filtres pour la page /recherche (Karim 2026-06-25).
 *
 * Remplace l'ancien popover ancré : on passe à un vrai modal centré
 * (UModal Nuxt UI v3) avec coins arrondis et un layout vertical aéré.
 *
 * Filtres affichés (toujours présents si data dispo) :
 *  - Ville
 *  - Catégorie / Type d'activité
 *
 * Filtres conditionnels :
 *  - Tranche de prix (adhesions uniquement — l'API publique des events
 *    n'expose pas de champ prix pour l'instant). Bornes en €.
 *  - Date (events uniquement) — date début + date fin sur start_date.
 *
 * État géré via v-model côté parent (open + chaque filtre).
 */

// Type importé de la SOURCE UNIQUE (`#shared/utils/listingTypes`) plutôt que
// redéclaré : cinq copies de cette union vivaient ici, et l'ajout du rayon
// « boutiques » (2026-08-17) les aurait fait diverger une à une.
import type { ValidType } from "#shared/utils/listingTypes";

const { t } = useI18n({ useScope: "local" });

interface Category {
  value: string;
  label: string;
  icon: string;
  /** Vignette ; vide = tuile à icône (« Tous », « Autre »). */
  image?: string;
  /** Nombre de résultats dans tout le rayon (API). */
  count?: number;
}

interface CityOption {
  slug: string;
  name: string;
  count: number;
}

interface PriceBucket {
  key: string;
  label: string;
  min: number | null;
  max: number | null;
}

interface DatePreset {
  key: string;
  label: string;
}

const props = defineProps<{
  open: boolean;
  activeType: ValidType;
  /** Thèmes choisis (vide = tous) — choix multiple. */
  selectedCategories: string[];
  categories: ReadonlyArray<Category>;
  /** Types d'activité (événements) — « all » en tête. */
  activityTypes?: ReadonlyArray<{ value: string; label: string }>;
  /** Types d'activité choisis (vide = tous) — choix multiple. */
  selectedActivityTypes?: string[];
  /** Liste des villes ayant ≥1 entité (filtre dynamique). */
  cities?: CityOption[];
  /** Slug de la ville actuellement sélectionnée. */
  selectedVille?: string | null;
  /** Tranches de prix dynamiques (adhésions uniquement, filtrées par
   *  présence d'au moins 1 prix dans la tranche). */
  priceBuckets?: PriceBucket[];
  /** Clé de la tranche actuellement sélectionnée (null = aucune). */
  selectedPriceBucket?: string | null;
  /** Presets de date dynamiques (events uniquement) — Aujourd'hui, Demain,
   *  Ce week-end, Cette semaine, etc. Filtrés par présence d'au moins 1
   *  événement dans la plage. */
  datePresets?: DatePreset[];
  /** Clé du preset date sélectionné (null = aucun). */
  selectedDatePreset?: string | null;
}>();

const emit = defineEmits<{
  "update:open": [v: boolean];
  "update:selectedCategories": [v: string[]];
  "update:selectedActivityTypes": [v: string[]];
  "update:selectedPriceBucket": [v: string | null];
  "update:selectedDatePreset": [v: string | null];
  "select-ville": [slug: string];
  "clear-ville": [];
  "reset-all": [];
}>();

const isOpen = computed({
  get: () => props.open,
  set: (v) => emit("update:open", v),
});

// ─── Ville : autocomplétion + « ma position » (2026-09-26) ──────────────
// Même logique et même rendu que la barre de recherche de l'en-tête
// (useRechercheVille / RechercheVilleSuggestions) : avant, ce champ ne
// filtrait que la liste des villes déjà présentes, sans géolocalisation.
const {
  requete: villeSearchQuery,
  suggestions: villeSuggestions,
  enCours: villeRechercheEnCours,
  geoEnCours,
  geoErreur,
  localiser,
} = useRechercheVille(() => props.cities);

/** Suggestions dépliées sous le champ (focus ou saisie). */
const villeListeOuverte = ref(false);

/**
 * Nom de la ville choisie : une commune trouvée par l'autocomplétion n'est
 * pas forcément dans `cities` (villes où Donoor a déjà des résultats).
 */
const nomVilleChoisie = ref<string | null>(null);
const libelleVilleChoisie = computed(() => {
  if (!props.selectedVille) return null;
  return (
    props.cities?.find((c) => c.slug === props.selectedVille)?.name ??
    nomVilleChoisie.value ??
    props.selectedVille.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
});

const choisirVille = (ville: { name: string; slug: string }) => {
  nomVilleChoisie.value = ville.name;
  villeSearchQuery.value = "";
  villeListeOuverte.value = false;
  emit("select-ville", ville.slug);
};

const villeDepuisPosition = async () => {
  const ville = await localiser();
  if (ville) choisirVille(ville);
};

/** Rayons où la ville a un sens (pas le mode « tout » ni les QR codes). */
const showVilleFilter = computed(
  () => props.activeType !== "tout" && props.activeType !== "qr-codes",
);

// PublicEvent n'expose pas de prix dans l'API publique → on garde le filtre
// prix sur adhésions uniquement (PublicPlan.price).
// On n'affiche la section que si on a au moins 1 tranche disponible
// (sinon, c'est qu'aucune adhésion n'a de prix → afficher des inputs vides
// n'aurait aucun sens).
const showPriceFilter = computed(
  () =>
    props.activeType === "adhesions" &&
    (props.priceBuckets?.length ?? 0) > 0,
);
const showDateFilter = computed(
  () =>
    props.activeType === "evenements" &&
    (props.datePresets?.length ?? 0) > 0,
);


/** Ajoute ou retire une valeur d'une sélection multiple. */
const basculer = (liste: readonly string[], val: string) =>
  liste.includes(val) ? liste.filter((v) => v !== val) : [...liste, val];
const themeChoisi = (val: string) => props.selectedCategories.includes(val);
const typesChoisis = computed(() => props.selectedActivityTypes ?? []);
const typeChoisi = (val: string) =>
  val === "all" ? typesChoisis.value.length === 0 : typesChoisis.value.includes(val);

// ─── Thèmes : une rangée qui défile (2026-09-26) ────────────────────────
// Une grille de 18 vignettes noyait la fenêtre, et « Voir les autres
// thèmes » cachait la moitié du choix (Karim). Tous les thèmes tiennent sur
// UNE rangée qui défile horizontalement, les plus fournis d'abord. Pas de
// tuile « Tous » (ne rien choisir = tous, « Réinitialiser » est là) ni
// « Autre » (pas un domaine qu'on cherche) — sauf si un lien l'a déjà choisie.
// Les thèmes sans résultat restent visibles ET cliquables, en fin de rangée
// (« · 0 ») : la liste complète dit ce qu'on peut chercher sur Donoor.
const themesTries = computed(() =>
  props.categories
    .filter(
      (c) =>
        c.value !== "all" && (c.value !== "autre" || themeChoisi("autre")),
    )
    .slice()
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0)),
);

/** Flèches (ordinateur) : une souris sans molette horizontale doit défiler. */
const rangeeThemes = ref<HTMLElement | null>(null);
const defilerThemes = (sens: 1 | -1) =>
  rangeeThemes.value?.scrollBy({ left: sens * 250, behavior: "smooth" });

/** État des flèches : y a-t-il de quoi défiler, avant / après ? */
const themesDefilent = ref(false);
const themesAvant = ref(false);
const themesApres = ref(false);
function majFlechesThemes() {
  const el = rangeeThemes.value;
  if (!el) return;
  themesDefilent.value = el.scrollWidth > el.clientWidth + 2;
  themesAvant.value = el.scrollLeft > 2;
  themesApres.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
}
// Recalcul à l'ouverture et quand la liste change (rayon, données).
watch(
  () => [props.open, themesTries.value.length],
  () => nextTick(majFlechesThemes),
  { immediate: true },
);

/** Choix multiple : un clic coche, un re-clic décoche. */
const basculerTheme = (val: string) =>
  emit("update:selectedCategories", basculer(props.selectedCategories, val));
const effacerThemes = () => emit("update:selectedCategories", []);
/** « Tous les types » vide la sélection ; un type se coche / décoche. */
const basculerType = (val: string) =>
  emit("update:selectedActivityTypes", val === "all" ? [] : basculer(typesChoisis.value, val));

const clearVille = () => {
  nomVilleChoisie.value = null;
  emit("clear-ville");
};

// Compteur de filtres actifs (badge + bouton "Tout réinitialiser")
const activeFilterCount = computed(() => {
  let n = 0;
  if (props.selectedVille) n++;
  n += props.selectedCategories.length;
  n += typesChoisis.value.length;
  if (props.selectedPriceBucket) n++;
  if (props.selectedDatePreset) n++;
  return n;
});

const selectPriceBucket = (key: string) => {
  // Re-clic sur la même tranche = désélection (toggle).
  emit(
    "update:selectedPriceBucket",
    props.selectedPriceBucket === key ? null : key,
  );
};

const selectDatePreset = (key: string) => {
  // Re-clic sur le même preset = désélection (toggle).
  emit(
    "update:selectedDatePreset",
    props.selectedDatePreset === key ? null : key,
  );
};

const resetAll = () => emit("reset-all");
</script>

<template>
  <UModal
    v-model:open="isOpen"
    :ui="{
      content: 'sm:max-w-lg sm:rounded-3xl! bg-white!',
      header: 'hidden!',
      body: 'p-0!',
    }"
  >
    <template #body>
      <div class="flex flex-col max-h-[85vh]">
        <!-- Header (sans border bottom — Karim 2026-06-25) -->
        <div class="px-6 py-5 flex items-center justify-between shrink-0">
          <div class="flex items-center gap-2">
            <h2 class="text-lg font-bold text-gray-900">{{ t('filtres.titre') }}</h2>
            <span
              v-if="activeFilterCount > 0"
              class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-main text-white text-[11px] font-bold"
            >
              {{ activeFilterCount }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="activeFilterCount > 0"
              type="button"
              class="text-xs font-medium text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline"
              @click="resetAll"
            >
              {{ t('filtres.toutReinitialiser') }}
            </button>
            <button
              type="button"
              :aria-label="t('filtres.fermer')"
              class="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
              @click="isOpen = false"
            >
              <UIcon name="i-lucide-x" class="size-5" />
            </button>
          </div>
        </div>

        <!-- Body scrollable -->
        <div class="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <!-- ─── Ville ──────────────────────────────────────────── -->
          <section v-if="showVilleFilter">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <UIcon name="i-lucide-map-pin" class="size-4 text-gray-500" />
                {{ t('ville.titre') }}
              </h3>
              <button
                v-if="selectedVille"
                class="text-xs text-main font-medium hover:underline"
                @click="clearVille"
              >
                {{ t('commun.reinitialiser') }}
              </button>
            </div>

            <!-- Ville choisie -->
            <div v-if="libelleVilleChoisie" class="mb-3">
              <span class="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full bg-main text-white text-xs font-medium">
                <UIcon name="i-lucide-map-pin" class="size-3.5" />
                {{ libelleVilleChoisie }}
                <button
                  type="button"
                  class="size-5 rounded-full hover:bg-white/20 flex items-center justify-center"
                  :aria-label="t('ville.retirer')"
                  @click="clearVille"
                >
                  <UIcon name="i-lucide-x" class="size-3.5" />
                </button>
              </span>
            </div>

            <div class="relative">
              <UIcon
                name="i-lucide-search"
                class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 size-4 pointer-events-none"
              />
              <input
                v-model="villeSearchQuery"
                type="text"
                inputmode="search"
                autocomplete="off"
                :placeholder="selectedVille ? t('ville.changer') : t('ville.rechercher')"
                class="w-full pl-10 pr-3 py-2.5 rounded-full border border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 focus:bg-white text-sm transition-colors"
                @focus="villeListeOuverte = true"
                @input="villeListeOuverte = true"
              />
            </div>

            <!-- Suggestions dépliées dans la fenêtre (une liste flottante y
                 serait rognée par le bord de la fenêtre). -->
            <div
              v-if="villeListeOuverte"
              class="mt-2 rounded-2xl border border-gray-100 p-2 max-h-72 overflow-y-auto"
            >
              <RechercheVilleSuggestions
                :requete="villeSearchQuery"
                :suggestions="villeSuggestions"
                :en-cours="villeRechercheEnCours"
                :geo-en-cours="geoEnCours"
                :geo-erreur="geoErreur"
                @choisir="choisirVille"
                @ma-position="villeDepuisPosition"
              />
            </div>
          </section>

          <!-- ─── Type d'activité (événements) ──────────────────── -->
          <section v-if="(activityTypes?.length ?? 0) > 1">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <UIcon name="i-lucide-ticket" class="size-4 text-gray-500" />
                {{ t('typeActivite.titre') }}
              </h3>
              <button
                v-if="typesChoisis.length > 0"
                class="text-xs text-main font-medium hover:underline"
                @click="basculerType('all')"
              >
                {{ t('commun.reinitialiser') }}
              </button>
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="t in activityTypes"
                :key="t.value"
                :class="[
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap',
                  typeChoisi(t.value)
                    ? 'bg-main text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                ]"
                :aria-pressed="typeChoisi(t.value)"
                @click="basculerType(t.value)"
              >
                {{ t.label }}
              </button>
            </div>
          </section>

          <!-- ─── Thème (liste commune à tous les rayons) ───────── -->
          <section v-if="categories.length > 0">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <UIcon name="i-lucide-shapes" class="size-4 text-gray-500" />
                {{ t('theme.titre') }}
              </h3>
              <div class="flex items-center gap-3">
                <button
                  v-if="selectedCategories.length > 0"
                  class="text-xs text-main font-medium hover:underline"
                  @click="effacerThemes"
                >
                  {{ t('commun.reinitialiser') }}
                </button>
                <!-- Flèches dans l'en-tête, jamais sur les vignettes (Karim
                     2026-09-26) ; ordinateur seulement, masquées quand tout
                     tient sans défiler. -->
                <div v-if="themesDefilent" class="hidden sm:flex items-center gap-1">
                  <button
                    type="button"
                    :aria-label="t('theme.precedents')"
                    :disabled="!themesAvant"
                    class="size-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-default"
                    @click="defilerThemes(-1)"
                  >
                    <UIcon name="i-lucide-chevron-left" class="size-4" />
                  </button>
                  <button
                    type="button"
                    :aria-label="t('theme.suivants')"
                    :disabled="!themesApres"
                    class="size-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-default"
                    @click="defilerThemes(1)"
                  >
                    <UIcon name="i-lucide-chevron-right" class="size-4" />
                  </button>
                </div>
              </div>
            </div>
            <!-- Deux lignes qui défilent, bord à bord dans la fenêtre. La
                 largeur des vignettes est CALCULÉE pour qu'à l'ouverture la
                 dernière colonne soit coupée en deux (2,5 colonnes sur
                 téléphone, 3,5 sur ordinateur) : on comprend d'un coup d'œil
                 qu'il faut faire glisser (Karim 2026-09-26). -->
            <div>
            <div
              ref="rangeeThemes"
              class="-mx-6 px-6 grid grid-rows-2 grid-flow-col auto-cols-[calc((100%+0.5rem)/2.5)] sm:auto-cols-[calc(100%/3.5)] gap-2 overflow-x-auto snap-x snap-mandatory scroll-px-6 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              @scroll.passive="majFlechesThemes"
            >
              <button
                v-for="cat in themesTries"
                :key="cat.value"
                type="button"
                :aria-pressed="themeChoisi(cat.value)"
                :class="[
                  'group relative snap-start rounded-lg overflow-hidden aspect-[3/2] text-left transition-all duration-200 m-0.5',
                  cat.image ? 'bg-gray-100' : 'bg-gray-100 hover:bg-gray-200',
                  themeChoisi(cat.value) ? 'ring-2 ring-main' : 'hover:shadow-md',
                ]"
                @click="basculerTheme(cat.value)"
              >
                <img
                  v-if="cat.image"
                  :src="cat.image"
                  :alt="cat.label"
                  class="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div
                  v-if="cat.image"
                  class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"
                />
                <span :class="['absolute left-2 right-2 bottom-1.5', cat.image ? 'text-white' : 'text-gray-700']">
                  <UIcon v-if="!cat.image" :name="cat.icon" class="size-3.5 mb-0.5" />
                  <span class="block text-[11px] font-semibold leading-tight line-clamp-2">{{ cat.label }}<span
                    v-if="cat.count !== undefined"
                    :class="['font-normal', cat.image ? 'text-white/75' : 'text-gray-500']"
                  > · {{ cat.count }}</span></span>
                </span>
                <span
                  v-if="themeChoisi(cat.value)"
                  class="absolute top-1 right-1 size-4 rounded-full bg-main text-white flex items-center justify-center"
                >
                  <UIcon name="i-lucide-check" class="size-3" />
                </span>
              </button>
            </div>
            </div>
          </section>

          <!-- ─── Tranche de prix (adhésions uniquement) ────────── -->
          <!-- Tranches DYNAMIQUES : on n'affiche que celles qui contiennent
               au moins une adhésion réelle (Karim 2026-06-25). -->
          <section v-if="showPriceFilter">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <UIcon name="i-lucide-euro" class="size-4 text-gray-500" />
                {{ t('prix.titre') }}
              </h3>
              <button
                v-if="selectedPriceBucket"
                class="text-xs text-main font-medium hover:underline"
                @click="emit('update:selectedPriceBucket', null)"
              >
                {{ t('commun.reinitialiser') }}
              </button>
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="bucket in priceBuckets"
                :key="bucket.key"
                type="button"
                :class="[
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap',
                  selectedPriceBucket === bucket.key
                    ? 'bg-main text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                ]"
                @click="selectPriceBucket(bucket.key)"
              >
                {{ bucket.label }}
              </button>
            </div>
          </section>

          <!-- ─── Date de l'événement (presets dynamiques) ──────── -->
          <!-- Liste de plages temporelles pré-faites avec radio :
               Aujourd'hui, Demain, Ce week-end, Cette semaine, etc.
               Seuls les presets contenant ≥1 événement réel sont affichés
               (Karim 2026-06-25). -->
          <section v-if="showDateFilter">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <UIcon name="i-lucide-calendar-days" class="size-4 text-gray-500" />
                {{ t('date.titre') }}
              </h3>
              <button
                v-if="selectedDatePreset"
                class="text-xs text-main font-medium hover:underline"
                @click="emit('update:selectedDatePreset', null)"
              >
                {{ t('commun.reinitialiser') }}
              </button>
            </div>
            <div class="flex flex-col gap-1">
              <!-- Option « Tout » : pas de filtre date = selectedDatePreset null -->
              <button
                type="button"
                class="flex items-center gap-3 px-2 py-2 rounded-lg text-sm text-gray-900 hover:bg-gray-50 transition-colors text-left"
                @click="emit('update:selectedDatePreset', null)"
              >
                <span
                  class="size-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors"
                  :class="!selectedDatePreset ? 'border-main' : 'border-gray-300'"
                >
                  <span
                    v-if="!selectedDatePreset"
                    class="size-2.5 rounded-full bg-main"
                  />
                </span>
                <span class="font-medium">{{ t('date.tout') }}</span>
              </button>
              <button
                v-for="preset in datePresets"
                :key="preset.key"
                type="button"
                class="flex items-center gap-3 px-2 py-2 rounded-lg text-sm text-gray-900 hover:bg-gray-50 transition-colors text-left"
                @click="selectDatePreset(preset.key)"
              >
                <span
                  class="size-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors"
                  :class="
                    selectedDatePreset === preset.key
                      ? 'border-main'
                      : 'border-gray-300'
                  "
                >
                  <span
                    v-if="selectedDatePreset === preset.key"
                    class="size-2.5 rounded-full bg-main"
                  />
                </span>
                <span class="font-medium">{{ preset.label }}</span>
              </button>
            </div>
          </section>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 shrink-0 bg-gray-50/50">
          <button
            type="button"
            class="px-5 py-2.5 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            @click="isOpen = false"
          >
            {{ t('pied.annuler') }}
          </button>
          <button
            type="button"
            class="px-6 py-2.5 rounded-full bg-main text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            @click="isOpen = false"
          >
            {{ t('pied.afficher') }}
          </button>
        </div>
      </div>
    </template>
  </UModal>
</template>

<i18n lang="json" src="../../../i18n/pages/components/recherche-page/RechercheFiltersModal.json"></i18n>
